/**
 * **The class-tier loadout redaction for a recorded resolution's SOURCES** — the walk that
 * `EncounterRoom.broadcastResolution` runs over `resolution.steps` once, viewer-agnostically,
 * beside the `about.weapon` strip it already ran (`backend/src/encounterRoomCore.ts`,
 * `redactHiddenWeapon`).
 *
 * **Why it exists.** `decisions/0081`'s disclosure rule says a player must not learn a
 * class-disclosure NPC's loadout, and {@link import("./projection").projectCombatantFor} enforces
 * that on the combatant itself — a `"class"`-tier NPC's projected `base` keeps `size`,
 * `allegiance` and only the rows of features the unit has revealed by using them
 * (`decisions/0207`), so its unrevealed `weapons`, `equippedSystems` and `npcFeatures` never reach
 * a player. The
 * RESOLUTION RECORD is a second, independent surface that named the same things anyway:
 * `ToHitStep.accuracyContributions[].name` carries the firing weapon's raw id when an NPC weapon
 * has innate Accuracy/Difficulty (`resolve/toHit.ts`), `DamageStep.source` carries
 * `{ name: weapon.name, id: weapon.id }` on every hit (`resolve/damageApply.ts`), and a rider
 * `DamageStep.source` carries the carrying feature's id (`effects/interpret.ts`, stamped from
 * `effects/triggerBus.ts`'s `firing`). The receipt therefore printed `DAMAGE   Bombard Cannon` for
 * a combatant whose whole loadout the dossier had just locked.
 *
 * **What it does.** For each recorded source it decides whether that source names something the
 * class tier strips off ITS OWN CARRIER's base, and if so replaces the recorded name with one of
 * two fixed sentinels ({@link UNDISCLOSED_WEAPON_NAME} / {@link UNDISCLOSED_FEATURE_NAME}) and
 * drops the raw id beside it. **Replace, never drop**: dropping an accuracy contribution would
 * break the itemization-balances-net invariant the narrator's golden pins, and dropping a weapon
 * hit's whole `source` degrades the main hit to the narrator's anonymous `"bonus damage"` — a
 * different claim, not a vaguer one.
 *
 * **Viewer-agnostic by construction.** There is no viewer argument here and there must not be
 * one: the durable feed (`SystemFeedEntry`) is ONE record for every reader and the COMMS card
 * renders off it with no encounter state loaded, so "strip per viewer" is not available
 * downstream. The GM sees the sentinel on the receipt too, and reads the real loadout where it
 * lives — the NPC inspector, the roster, `lvtt encounter show --json`. See
 * `projects/accuracy-contribution-weapon-redaction.md` Settled 1.
 *
 * **Pure and non-mutating.** `EncounterHistory` holds the same step arrays the room broadcasts, so
 * a mutation here would rewrite the authoritative record. Every rewritten step is a fresh object;
 * untouched steps come back by identity.
 */

import type { Combatant, CombatantId } from "./types";
import type { AccuracyContribution, CostWaivedStep, LinkStep, ResolutionAbout, ResolvedStep, SaveContribution } from "./steps";
import { UNDISCLOSED_FEATURE_NAME, UNDISCLOSED_WEAPON_NAME } from "./steps";

/**
 * The two name sets a class-tier NPC's record must not disclose, split by which sentinel a match
 * earns. Built by {@link undisclosedSourceNames} from one combatant's frozen base; consulted by
 * {@link redactUndisclosedSteps} against the source's OWN CARRIER, never against the attacker.
 */
export type UndisclosedSourceNames = {
  /** `base.weapons`' ids and display names (and their alternate profiles'). */
  weapons: Set<string>;
  /** `base.equippedSystems`' ids and names, `base.npcFeatures`' names, `base.npcFeatureEffectIds`. */
  features: Set<string>;
};

/**
 * Derive one combatant's undisclosed-source sets from **exactly the base rows the class tier
 * strips** — `weapons`, `equippedSystems`, `npcFeatures` and `npcFeatureEffectIds`, minus every
 * row whose id is in `revealedFeatureIds` (`decisions/0207`). A revealed weapon leaves with its
 * display name and its alternate profiles' ids and names.
 *
 * **Paired with {@link import("./projection").projectCombatantFor}'s class-tier branch**, which is
 * the authority for what the class tier hides: that function builds `ClassTierBase` (keeping only
 * `size` and `allegiance`) and this one enumerates what it dropped. **A change to one must change
 * the other** — a new stripped field that names something the record can carry would leak through
 * here silently. `projectCombatantFor` carries the mirror-image comment, and
 * `resolutionRedaction.test.ts` pins the pairing.
 *
 * Both ids AND display names go in, because the record's `name` fields are not uniformly one or
 * the other: a weapon damage source records `weapon.name` (a real display name), an accuracy
 * contribution records `weapon.id`, and a rider records `h.effect.name ?? h.entityId ?? h.name`
 * (`effects/triggerBus.ts`) — usually the carrying entity's id, but an authored clause `name`
 * would be a human string.
 *
 * **Residual gap, accepted:** an authored rider clause whose `name` equals none of the frozen
 * display names or ids here still leaks. No authored NPC-feature entry in the shipped sidecar sets
 * a clause `name`, so every fielded rider today is caught by its `npcFeatureEffectIds` id; a
 * homebrew entry that does set one is the uncovered case.
 */
export function undisclosedSourceNames(combatant: Combatant): UndisclosedSourceNames {
  const weapons = new Set<string>();
  const features = new Set<string>();
  const revealed = new Set(combatant.revealedFeatureIds ?? []);
  for (const w of combatant.base.weapons ?? []) {
    if (revealed.has(w.id)) continue;
    weapons.add(w.id);
    weapons.add(w.name);
    // A multi-profile weapon's alternate firing modes are what `selectWeaponProfile` hands the
    // resolver, so an alt profile's own id/name is equally recordable and equally hidden.
    for (const p of w.profiles ?? []) {
      weapons.add(p.id);
      weapons.add(p.name);
    }
  }
  for (const s of combatant.base.equippedSystems ?? []) {
    if (revealed.has(s.id)) continue;
    features.add(s.id);
    features.add(s.name);
  }
  for (const f of combatant.base.npcFeatures ?? []) {
    if (f.id !== undefined && revealed.has(f.id)) continue;
    features.add(f.name);
    if (f.id !== undefined) features.add(f.id);
  }
  for (const id of combatant.base.npcFeatureEffectIds ?? []) if (!revealed.has(id)) features.add(id);
  weapons.delete("");
  features.delete("");
  return { weapons, features };
}

/** Which sentinel a recorded source earns, or `undefined` when it names nothing the tier hides. */
function sentinelFor(sets: UndisclosedSourceNames, ...names: (string | undefined)[]): string | undefined {
  for (const n of names) {
    if (n !== undefined && sets.weapons.has(n)) return UNDISCLOSED_WEAPON_NAME;
  }
  for (const n of names) {
    if (n !== undefined && sets.features.has(n)) return UNDISCLOSED_FEATURE_NAME;
  }
  return undefined;
}

/**
 * Rewrite one contribution's `name` when its OWN carrier's tier hides it; identity otherwise.
 * Exported for `attackForecastMask.ts`, which masks a forecast's contributions with this same rule.
 */
export function redactContribution<C extends AccuracyContribution | SaveContribution>(
  c: C,
  setFor: (id: CombatantId) => UndisclosedSourceNames | undefined,
): C {
  const sets = setFor(c.carrier);
  if (sets === undefined) return c;
  const sentinel = sentinelFor(sets, c.name);
  return sentinel === undefined ? c : { ...c, name: sentinel };
}

/** Rewrite a list of contributions, returning the SAME array when nothing in it moved. */
export function redactContributions<C extends AccuracyContribution | SaveContribution>(
  list: C[] | undefined,
  setFor: (id: CombatantId) => UndisclosedSourceNames | undefined,
): C[] | undefined {
  if (list === undefined) return undefined;
  const next = list.map((c) => redactContribution(c, setFor));
  return next.some((c, i) => c !== list[i]) ? next : list;
}

/**
 * Walk a recorded resolution and replace every source that names a class-tier-hidden weapon,
 * system, trait, reaction or tech feature with its sentinel.
 *
 * `setFor` answers "what does THIS combatant's tier hide?", returning `undefined` for every
 * combatant whose record is disclosed (a `loadout`/`full` NPC, any non-NPC, or an id the room no
 * longer has state for). The caller owns that predicate so the room's disclosure gate lives in one
 * place; this walk owns only the rewrite.
 *
 * **Per CARRIER, not per actor.** A contribution's `carrier` is the combatant whose modifier
 * folded — ordinarily the attacker, but a `confer`'d contribution's carrier is the conferring
 * ally, so a class-tier NPC buffing a PC's attack is covered by the same walk while the PC's own
 * `tg_accurate` beside it is not (its carrier's sets are never consulted). A damage source's
 * `carrier` means the same thing, and is **omitted for the attacker's own weapon or rider** — a
 * carrier-less source therefore falls back to the attacker of the segment's `to_hit`, and
 * `fallbackActor` (pass `about.actor`) covers a step list whose first `to_hit` has not been seen
 * yet, or one with no `to_hit` at all.
 *
 * Discrimination is **by which set matched**, deliberately not by reading the source's own
 * weapon/rider discriminant: set membership is the more direct question ("is this one of the
 * things the tier hides?").
 *
 * Returns a new array; every step it did not rewrite is the same object it was handed.
 */
export function redactUndisclosedSteps(
  steps: readonly ResolvedStep[],
  setFor: (id: CombatantId) => UndisclosedSourceNames | undefined,
  fallbackActor?: CombatantId,
): ResolvedStep[] {
  let segmentAttacker = fallbackActor;
  return steps.map((step) => {
    if (step.op === "to_hit") {
      segmentAttacker = step.attacker;
      const contributions = redactContributions(step.accuracyContributions, setFor);
      return contributions === step.accuracyContributions ? step : { ...step, accuracyContributions: contributions };
    }
    if (step.op === "save") {
      const contributions = redactContributions(step.accuracyContributions, setFor);
      return contributions === step.accuracyContributions ? step : { ...step, accuracyContributions: contributions };
    }
    if (step.op === "damage") {
      const source = step.source;
      if (source === undefined) return step;
      const carrier = source.carrier ?? segmentAttacker;
      if (carrier === undefined) return step;
      const sets = setFor(carrier);
      if (sets === undefined) return step;
      const sentinel = sentinelFor(sets, source.name, source.id);
      if (sentinel === undefined) return step;
      // The raw id goes with the name — keeping it would hand back exactly what the name just
      // stopped disclosing. Everything else on the source (its weapon/rider discriminant, its
      // `carrier`) is untouched: the narrator still knows this was the main hit rather than a
      // rider, and the fog-of-war walk still sees the same combatant ids.
      const { id: _id, ...rest } = source;
      return { ...step, source: { ...rest, name: sentinel } };
    }
    // A chain `link` (`decisions/0210`) names the acting unit's weapon and the clause that forced it
    // (Aggression's Chain Axe), so it is masked by the same per-carrier rule as a damage source.
    if (step.op === "link") return redactLink(step, setFor);
    // A Recharge feature's `discharge_set` keys its ledger slot by the feature's affordance key
    // (`npcf_charge_cataphract:0`), which the DISCHARGE row prints (`narration-ledger-retirement`).
    // A weapon instance key (`npc:0`) names no loadout item and passes.
    if (step.op === "discharge_set") {
      const sets = setFor(step.target);
      if (sets === undefined) return step;
      const sentinel = sentinelFor(sets, step.key.split(":")[0]);
      return sentinel === undefined ? step : { ...step, key: sentinel };
    }
    if (step.op === "cost_waived") return redactCostWaived(step, setFor);
    return step;
  });
}

/**
 * Rewrite a `cost_waived` step (`decisions/0211`) when it names something hidden: its replacing
 * clause (`by`, masked by its carrier's tier, the raw id dropped with the name) and the pool it
 * would have spent from (`key`, a system id, masked by the cost's owner's tier).
 */
function redactCostWaived(step: CostWaivedStep, setFor: (id: CombatantId) => UndisclosedSourceNames | undefined): CostWaivedStep {
  let out = step;
  const bySets = setFor(step.by.carrier);
  const bySentinel = bySets !== undefined ? sentinelFor(bySets, step.by.name, step.by.id) : undefined;
  if (bySentinel !== undefined) out = { ...out, by: { carrier: step.by.carrier, name: bySentinel } };
  const keySets = setFor(step.target);
  const keySentinel = keySets !== undefined ? sentinelFor(keySets, step.key) : undefined;
  if (keySentinel !== undefined) out = { ...out, key: keySentinel };
  return out;
}

/** Rewrite a `link` step's weapon (the actor's) and feature (its carrier's) to their sentinels when hidden. */
function redactLink(step: LinkStep, setFor: (id: CombatantId) => UndisclosedSourceNames | undefined): LinkStep {
  let out = step;
  const actorSets = step.weapon !== undefined ? setFor(step.actor) : undefined;
  const weaponSentinel = actorSets !== undefined && step.weapon !== undefined ? sentinelFor(actorSets, step.weapon.name, step.weapon.id) : undefined;
  if (weaponSentinel !== undefined && step.weapon !== undefined) {
    const { id: _id, ...rest } = step.weapon;
    out = { ...out, weapon: { ...rest, name: weaponSentinel } };
  }
  const featureSets = step.feature !== undefined ? setFor(step.feature.carrier) : undefined;
  const featureSentinel = featureSets !== undefined && step.feature !== undefined ? sentinelFor(featureSets, step.feature.name, step.feature.id) : undefined;
  if (featureSentinel !== undefined && step.feature !== undefined) {
    const { id: _id, ...rest } = step.feature;
    out = { ...out, feature: { ...rest, name: featureSentinel } };
  }
  return out;
}

/**
 * The class-tier rule for a recorded tech OPTION (`ResolutionAbout.techOption`,
 * `comms-attack-reaction-cards` 1.3). The caller decides WHETHER to apply it, with the same
 * predicate that drops `about.weapon` (`EncounterRoom.classTierMasked`); this owns only the
 * rewrite, so the sentinel shape lives beside the step walk's.
 *
 * **Replace, never drop**, for the step walk's reason: the card reads
 * `Invade · undisclosed feature`, which still says a tech option fired, where dropping the field
 * would read as an old entry that never recorded one. The raw `id` goes with the name; `action`
 * (a core action's RAW name, e.g. "Invade") stays.
 */
export function redactTechOption(techOption: ResolutionAbout["techOption"]): ResolutionAbout["techOption"] {
  if (techOption === undefined) return undefined;
  return { name: UNDISCLOSED_FEATURE_NAME, ...(techOption.action !== undefined ? { action: techOption.action } : {}) };
}

/**
 * The class-tier rule for a recorded ITEM (`ResolutionAbout.item`, `narration-ledger-retirement`
 * 2.2), applied under the same predicate as {@link redactTechOption}. The name becomes
 * {@link UNDISCLOSED_FEATURE_NAME} and the raw `id` goes. `about.action` goes too when it spells
 * that same id (an activation records its raw affordance key, `npcf_impale_cataphract:0`), or the
 * redaction would leave the feature readable one field over.
 */
export function redactAboutItem(about: ResolutionAbout): ResolutionAbout {
  const { item, action, ...rest } = about;
  if (item === undefined) return about;
  const id = item.id;
  const actionNamesItem = id !== undefined && action !== undefined && (action === id || action.startsWith(`${id}:`));
  return { ...rest, ...(action !== undefined && !actionNamesItem ? { action } : {}), item: { name: UNDISCLOSED_FEATURE_NAME } };
}
